// src/components/NadscriptionCampaigns.js
import React, { useState, useEffect } from 'react';

const NADSCRIPTIONS_API = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/nadscriptions`
  : 'https://api.doctordripp.com/api/nadscriptions';

function NadscriptionCampaigns({ jwtToken }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    campaign_name: '',
    collection_title: '',
    description: '',
    max_supply: '',
    payment_address: '0x6d873a14a470dd969d7c76a2e088169ab2a1d7ae',
    mint_price_eth: '0.1'
  });
  
  // Unconfirmed mints state
  const [unconfirmedMints, setUnconfirmedMints] = useState([]);
  const [loadingUnconfirmed, setLoadingUnconfirmed] = useState(false);
  const [verifyingTx, setVerifyingTx] = useState(null);
  const [showUnconfirmed, setShowUnconfirmed] = useState(false);

  useEffect(() => {
    loadCampaigns();
    loadUnconfirmedMints();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${NADSCRIPTIONS_API}?action=list-campaigns`);
      const data = await response.json();
      
      if (data.success && data.campaigns) {
        setCampaigns(data.campaigns);
        setError('');
      } else {
        setError('Failed to load campaigns');
      }
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError('Error loading campaigns: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUnconfirmedMints = async () => {
    try {
      setLoadingUnconfirmed(true);
      const response = await fetch(`${NADSCRIPTIONS_API}?action=get-unconfirmed-mints&campaignName=nadscriptions-genesis`);
      const data = await response.json();
      
      if (data.success) {
        setUnconfirmedMints(data.mints || []);
      }
    } catch (err) {
      console.error('Error loading unconfirmed mints:', err);
    } finally {
      setLoadingUnconfirmed(false);
    }
  };

  const verifyMint = async (inscriptionTxHash) => {
    try {
      setVerifyingTx(inscriptionTxHash);
      const response = await fetch(`${NADSCRIPTIONS_API}?action=verify-mint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inscriptionTxHash })
      });
      
      const data = await response.json();
      
      if (data.success && data.verified) {
        alert(`✅ Transaction verified! Block: ${data.blockNumber}`);
        loadUnconfirmedMints(); // Refresh the list
      } else {
        alert(`❌ Could not verify: ${data.message || 'Transaction not found on-chain'}`);
      }
    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    } finally {
      setVerifyingTx(null);
    }
  };

  const verifyAllMints = async () => {
    if (!window.confirm(`Verify all ${unconfirmedMints.length} unconfirmed mints? This may take a while.`)) {
      return;
    }
    
    let verified = 0;
    let failed = 0;
    
    for (const mint of unconfirmedMints) {
      try {
        setVerifyingTx(mint.inscription_tx_hash);
        const response = await fetch(`${NADSCRIPTIONS_API}?action=verify-mint`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inscriptionTxHash: mint.inscription_tx_hash })
        });
        
        const data = await response.json();
        if (data.success && data.verified) {
          verified++;
        } else {
          failed++;
        }
      } catch (err) {
        failed++;
      }
      // Small delay between requests
      await new Promise(r => setTimeout(r, 500));
    }
    
    setVerifyingTx(null);
    alert(`✅ Verified: ${verified}\n❌ Failed: ${failed}`);
    loadUnconfirmedMints();
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${NADSCRIPTIONS_API}?action=create-campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignName: newCampaign.campaign_name,
          collectionTitle: newCampaign.collection_title,
          description: newCampaign.description || null,
          maxSupply: newCampaign.max_supply ? parseInt(newCampaign.max_supply) : null,
          paymentAddress: newCampaign.payment_address,
          mintPriceEth: parseFloat(newCampaign.mint_price_eth) || 0,
          startingMintNumber: 1
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert('✅ Campaign created successfully!');
        setShowCreateForm(false);
        setNewCampaign({
          campaign_name: '',
          collection_title: '',
          description: '',
          max_supply: '',
          payment_address: '0x6d873a14a470dd969d7c76a2e088169ab2a1d7ae',
          mint_price_eth: '0.1'
        });
        loadCampaigns();
      } else {
        alert('❌ Error: ' + (data.error || 'Failed to create campaign'));
      }
    } catch (err) {
      alert('❌ Error: ' + err.message);
    }
  };

  const toggleCampaignStatus = async (campaignName, newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus ? 'enable' : 'disable'} this campaign?`)) {
      return;
    }

    try {
      const response = await fetch(`${NADSCRIPTIONS_API}?action=update-campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignName,
          isActive: newStatus
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`✅ Campaign ${newStatus ? 'enabled' : 'disabled'} successfully!`);
        loadCampaigns();
      } else {
        alert('❌ Error: ' + (data.error || 'Update failed'));
      }
    } catch (err) {
      alert('❌ Error: ' + err.message);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>Loading campaigns...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ color: '#ff6b6b', marginBottom: '1rem' }}>❌ {error}</div>
        <button onClick={loadCampaigns} style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: '#667eea', color: 'white', cursor: 'pointer' }}>
          Try Again
        </button>
      </div>
    );
  }

  if (showCreateForm) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h3 style={{ color: '#06d6a0', marginBottom: '1.5rem' }}>Create New Campaign</h3>
          
          <form onSubmit={handleCreateCampaign} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Campaign Name *</label>
              <input
                type="text"
                value={newCampaign.campaign_name}
                onChange={(e) => setNewCampaign({ ...newCampaign, campaign_name: e.target.value })}
                required
                placeholder="e.g., nadscriptions-series-2"
                style={{ width: '100%', padding: '10px', background: '#333', border: '1px solid #555', borderRadius: '8px', color: 'white' }}
              />
              <small style={{ color: '#999' }}>Use lowercase with hyphens (internal identifier)</small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Collection Title *</label>
              <input
                type="text"
                value={newCampaign.collection_title}
                onChange={(e) => setNewCampaign({ ...newCampaign, collection_title: e.target.value })}
                required
                placeholder="e.g., DOCTORdripp Genesis Skulls"
                style={{ width: '100%', padding: '10px', background: '#333', border: '1px solid #555', borderRadius: '8px', color: 'white' }}
              />
              <small style={{ color: '#999' }}>Display name shown to users on the launchpad</small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Description</label>
              <textarea
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                placeholder="e.g., The Second Series of Nadscriptions"
                style={{ width: '100%', padding: '10px', background: '#333', border: '1px solid #555', borderRadius: '8px', color: 'white', minHeight: '80px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Max Supply</label>
              <input
                type="number"
                value={newCampaign.max_supply}
                onChange={(e) => setNewCampaign({ ...newCampaign, max_supply: e.target.value })}
                placeholder="Leave blank for unlimited"
                min="1"
                style={{ width: '100%', padding: '10px', background: '#333', border: '1px solid #555', borderRadius: '8px', color: 'white' }}
              />
              <small style={{ color: '#999' }}>Leave blank for unlimited supply</small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Payment Address *</label>
              <input
                type="text"
                value={newCampaign.payment_address}
                onChange={(e) => setNewCampaign({ ...newCampaign, payment_address: e.target.value })}
                required
                placeholder="0x..."
                style={{ width: '100%', padding: '10px', background: '#333', border: '1px solid #555', borderRadius: '8px', color: 'white' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Mint Price (MON)</label>
              <input
                type="number"
                step="0.000001"
                value={newCampaign.mint_price_eth}
                onChange={(e) => setNewCampaign({ ...newCampaign, mint_price_eth: e.target.value })}
                placeholder="0.1"
                min="0"
                style={{ width: '100%', padding: '10px', background: '#333', border: '1px solid #555', borderRadius: '8px', color: 'white' }}
              />
              <small style={{ color: '#999' }}>Set to 0 for free mints</small>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: '#06d6a0', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Create Campaign
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: '#666', color: 'white', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#06d6a0' }}>💀 Nadscription Campaigns</h3>
        <button
          onClick={() => setShowCreateForm(true)}
          style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#06d6a0', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
        >
          + Create New Campaign
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
              <th style={{ padding: '1rem', textAlign: 'left', color: '#aaa' }}>Campaign Name</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: '#aaa' }}>Current Mint #</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: '#aaa' }}>Max Supply</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: '#aaa' }}>Price</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: '#aaa' }}>Status</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: '#aaa' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map(campaign => {
              const minted = campaign.current_mint_number - 1;
              const progress = campaign.max_supply ? `${minted}/${campaign.max_supply}` : `${minted} minted`;
              const percentage = campaign.max_supply ? Math.round((minted / campaign.max_supply) * 100) : 0;
              const isFree = campaign.mint_price_eth === 0 || campaign.mint_price_eth === '0';

              return (
                <tr key={campaign.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 'bold', color: '#06d6a0' }}>{campaign.campaign_name}</div>
                    {campaign.description && (
                      <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>{campaign.description}</div>
                    )}
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 'bold', color: 'white' }}>{campaign.current_mint_number}</td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ color: 'white' }}>{campaign.max_supply || '∞ Unlimited'}</div>
                    {campaign.max_supply && (
                      <div style={{ fontSize: '0.85rem', color: '#ffc107', marginTop: '0.25rem' }}>
                        {progress} ({percentage}%)
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ color: isFree ? '#4CAF50' : 'white', fontWeight: 'bold' }}>
                      {isFree ? 'FREE' : `${campaign.mint_price_eth} MON`}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                      + GAS FEE
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      background: campaign.is_active ? 'rgba(6, 214, 160, 0.2)' : 'rgba(255, 107, 107, 0.2)',
                      color: campaign.is_active ? '#06d6a0' : '#ff6b6b'
                    }}>
                      {campaign.is_active ? '✅ Active' : '⏸️ Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => toggleCampaignStatus(campaign.campaign_name, !campaign.is_active)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        background: campaign.is_active ? '#ff6b6b' : '#28a745',
                        color: 'white',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      {campaign.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Unconfirmed Mints Section */}
      <div style={{
        marginTop: '2rem',
        padding: '1.5rem',
        background: unconfirmedMints.length > 0 ? 'rgba(255, 107, 107, 0.1)' : 'rgba(6, 214, 160, 0.1)',
        borderRadius: '12px',
        border: `1px solid ${unconfirmedMints.length > 0 ? 'rgba(255, 107, 107, 0.3)' : 'rgba(6, 214, 160, 0.3)'}`
      }}>
        <div 
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowUnconfirmed(!showUnconfirmed)}
        >
          <h4 style={{ color: unconfirmedMints.length > 0 ? '#ff6b6b' : '#06d6a0', margin: 0 }}>
            {unconfirmedMints.length > 0 ? '⚠️' : '✅'} Unconfirmed Mints ({unconfirmedMints.length})
          </h4>
          <span style={{ color: '#aaa' }}>{showUnconfirmed ? '▼' : '►'}</span>
        </div>
        
        {showUnconfirmed && (
          <div style={{ marginTop: '1rem' }}>
            {loadingUnconfirmed ? (
              <div style={{ color: '#aaa', textAlign: 'center', padding: '1rem' }}>Loading...</div>
            ) : unconfirmedMints.length === 0 ? (
              <div style={{ color: '#06d6a0', textAlign: 'center', padding: '1rem' }}>
                All mints are confirmed! 🎉
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={verifyAllMints}
                    disabled={verifyingTx !== null}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: '#667eea',
                      color: 'white',
                      fontWeight: 'bold',
                      cursor: verifyingTx ? 'not-allowed' : 'pointer',
                      opacity: verifyingTx ? 0.6 : 1
                    }}
                  >
                    {verifyingTx ? 'Verifying...' : 'Verify All'}
                  </button>
                  <button
                    onClick={loadUnconfirmedMints}
                    disabled={loadingUnconfirmed}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: '#444',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Refresh
                  </button>
                </div>
                
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <th style={{ padding: '0.5rem', textAlign: 'left', color: '#aaa' }}>Mint #</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', color: '#aaa' }}>TX Hash</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', color: '#aaa' }}>Wallet</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', color: '#aaa' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unconfirmedMints.map(mint => (
                        <tr key={mint.inscription_tx_hash} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '0.5rem', color: '#ffc107', fontWeight: 'bold' }}>#{mint.mint_number}</td>
                          <td style={{ padding: '0.5rem' }}>
                            <a 
                              href={`https://monadscan.com/tx/${mint.inscription_tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#06d6a0', fontSize: '0.8rem' }}
                            >
                              {mint.inscription_tx_hash.substring(0, 10)}...{mint.inscription_tx_hash.substring(58)}
                            </a>
                          </td>
                          <td style={{ padding: '0.5rem', color: '#999', fontSize: '0.8rem' }}>
                            {mint.wallet_address?.substring(0, 6)}...{mint.wallet_address?.substring(38)}
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            <button
                              onClick={() => verifyMint(mint.inscription_tx_hash)}
                              disabled={verifyingTx === mint.inscription_tx_hash}
                              style={{
                                padding: '4px 10px',
                                borderRadius: '4px',
                                border: 'none',
                                background: verifyingTx === mint.inscription_tx_hash ? '#666' : '#28a745',
                                color: 'white',
                                fontSize: '0.75rem',
                                cursor: verifyingTx === mint.inscription_tx_hash ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {verifyingTx === mint.inscription_tx_hash ? '...' : '✓ Verify'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            
            <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#999' }}>
              Unconfirmed mints are transactions that were logged but blockchain confirmation wasn't received.
              Click "Verify" to check on-chain status and confirm.
            </div>
          </div>
        )}
      </div>

      <div style={{
        marginTop: '2rem',
        padding: '1.5rem',
        background: 'rgba(255, 193, 7, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 193, 7, 0.3)'
      }}>
        <h4 style={{ color: '#ffc107', marginBottom: '0.75rem' }}>ℹ️ Campaign Management Tips</h4>
        <ul style={{ color: '#ccc', lineHeight: '1.8', marginLeft: '1.5rem', fontSize: '0.9rem' }}>
          <li>Active campaigns can be minted on the launchpad</li>
          <li>Mint numbers increment atomically (no duplicates possible)</li>
          <li>Set max supply to limit collection size</li>
          <li>Payment address receives all mint fees (set to 0 for free)</li>
          <li>Launchpad: <a href="https://doctordripp.com/nadscriptions" target="_blank" rel="noopener noreferrer" style={{ color: '#06d6a0' }}>doctordripp.com/nadscriptions</a></li>
        </ul>
      </div>
    </div>
  );
}

export default NadscriptionCampaigns;

